import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Circle, FileText, ExternalLink, Notebook, Save, Loader2 } from 'lucide-react';
import { portalApi, formatApiError } from '../../contexts/PortalAuthContext';
import { toast } from 'sonner';

export default function PortalModule() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState([]);

  // Inline journal entry composer
  const [journalTitle, setJournalTitle] = useState('');
  const [journalContent, setJournalContent] = useState('');
  const [savingJournal, setSavingJournal] = useState(false);

  const reload = async () => {
    try {
      const { data } = await portalApi.get(`/modules/${moduleId}`);
      setM(data);
      setCompleted(data.progress?.completed_task_ids || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [moduleId]); // eslint-disable-line

  const toggleTask = async (taskId) => {
    const isDone = completed.includes(taskId);
    // Optimistic update
    setCompleted(prev => isDone ? prev.filter(t => t !== taskId) : [...prev, taskId]);
    try {
      await portalApi.post(`/modules/${moduleId}/tasks/${taskId}`, { completed: !isDone });
    } catch (e) {
      // revert
      setCompleted(prev => isDone ? [...prev, taskId] : prev.filter(t => t !== taskId));
      toast.error(formatApiError(e));
    }
  };

  const saveJournal = async (e) => {
    e.preventDefault();
    if (!journalTitle.trim() || !journalContent.trim()) return;
    setSavingJournal(true);
    try {
      await portalApi.post('/journal', {
        title: journalTitle,
        content: journalContent,
        module_id: moduleId,
      });
      setJournalTitle('');
      setJournalContent('');
      toast.success('Journal entry saved');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingJournal(false);
    }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm">Loading module…</div>;
  }
  if (!m) return <div className="text-slate-400">Module not found.</div>;

  const tasksDone = completed.length;
  const tasksTotal = m.tasks?.length || 0;
  const pct = tasksTotal ? Math.round(100 * tasksDone / tasksTotal) : 0;

  return (
    <div data-testid="portal-module-detail">
      <button onClick={() => navigate('/portal/modules')} className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2 mb-6">
        <ArrowLeft className="w-4 h-4" /> All modules
      </button>

      <div className="mb-2 text-xs uppercase tracking-widest text-amber-400">
        Module {String(m.number).padStart(2, '0')}
      </div>
      <h1 className="text-3xl sm:text-4xl font-semibold text-white">{m.title}</h1>
      {m.subtitle && <p className="text-slate-400 mt-2 text-lg">{m.subtitle}</p>}

      {/* Video */}
      <div className="mt-8 rounded-xl overflow-hidden bg-black border border-slate-800 aspect-video">
        {m.video_file ? (
          <video
            src={`${process.env.REACT_APP_BACKEND_URL}${m.video_file}`}
            controls
            className="w-full h-full"
            data-testid="portal-video-player"
          />
        ) : m.video_url ? (
          isEmbeddable(m.video_url) ? (
            <iframe
              src={toEmbed(m.video_url)}
              title={m.title}
              className="w-full h-full"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              data-testid="portal-video-iframe"
            />
          ) : (
            <video src={m.video_url} controls className="w-full h-full" data-testid="portal-video-player" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
            Video coming soon
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        {/* Description + tasks */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <h2 className="text-lg font-semibold text-white mb-3">What you&apos;ll cover</h2>
            <p className="text-slate-300 whitespace-pre-line">{m.description}</p>
          </div>

          {/* Tasks */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Tasks ({tasksDone}/{tasksTotal})</h2>
              <span className="text-xs text-slate-400">{pct}% complete</span>
            </div>
            <div className="h-1 bg-slate-800 rounded-full overflow-hidden mb-5">
              <div className="h-full bg-gradient-to-r from-amber-500 to-orange-600" style={{ width: `${pct}%` }} />
            </div>
            <ul className="space-y-3">
              {m.tasks?.map(t => {
                const done = completed.includes(t.id);
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => toggleTask(t.id)}
                      className="w-full text-left flex items-start gap-3 p-3 rounded-lg hover:bg-slate-800/60 transition"
                      data-testid={`task-toggle-${t.id}`}
                    >
                      {done
                        ? <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                        : <Circle className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <div className={`text-sm ${done ? 'line-through text-slate-500' : 'text-white'}`}>
                          {t.title}
                        </div>
                        {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                      </div>
                    </button>
                  </li>
                );
              })}
              {tasksTotal === 0 && <li className="text-slate-500 text-sm">No tasks yet for this module.</li>}
            </ul>
          </div>

          {/* Inline journal composer */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Notebook className="w-5 h-5 text-sky-400" />
              <h2 className="text-lg font-semibold text-white">Add a journal entry</h2>
            </div>
            <p className="text-xs text-slate-400 mb-4">Capture wins, blockers, ideas, or questions tied to this module.</p>
            <form onSubmit={saveJournal} className="space-y-3" data-testid="portal-module-journal-form">
              <input
                type="text"
                placeholder="Entry title"
                value={journalTitle}
                onChange={e => setJournalTitle(e.target.value)}
                maxLength={140}
                required
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none"
                data-testid="journal-title-input"
              />
              <textarea
                placeholder="What did you learn / build / get stuck on?"
                value={journalContent}
                onChange={e => setJournalContent(e.target.value)}
                rows={4}
                required
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none resize-y"
                data-testid="journal-content-input"
              />
              <button
                type="submit"
                disabled={savingJournal}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-60"
                data-testid="journal-save-btn"
              >
                {savingJournal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save entry
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar: resources */}
        <aside className="space-y-4">
          {m.workbook_url && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${m.workbook_url}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/40 transition"
              data-testid="portal-workbook-link"
            >
              <FileText className="w-5 h-5 text-amber-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-white">Workbook</div>
                <div className="text-xs text-slate-400">Download PDF</div>
              </div>
            </a>
          )}
          {(m.resources || []).map((r, i) => (
            <a
              key={i}
              href={r.url}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/40 transition"
            >
              <ExternalLink className="w-5 h-5 text-sky-400 shrink-0" />
              <div className="text-sm text-white truncate">{r.title}</div>
            </a>
          ))}
          {!m.workbook_url && (!m.resources || m.resources.length === 0) && (
            <div className="text-slate-500 text-sm">Resources will appear here as we add them.</div>
          )}

          <Link
            to="/portal/journal"
            className="block text-center text-sm text-amber-400 hover:underline pt-2"
          >
            View all your journal entries →
          </Link>
        </aside>
      </div>
    </div>
  );
}

function isEmbeddable(url) {
  return /youtube\.com|youtu\.be|vimeo\.com|loom\.com|wistia|bunnycdn|mediadelivery\.net/.test(url);
}

function toEmbed(url) {
  // YouTube
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?rel=0`;
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  // Loom
  m = url.match(/loom\.com\/share\/([\w-]+)/);
  if (m) return `https://www.loom.com/embed/${m[1]}`;
  // Bunny iframe is already a player URL
  return url;
}
